import { ChangeDetectionStrategy, Component, computed, input, numberAttribute } from '@angular/core';

/**
 * Inline icon set.
 *
 * Drawn on a 24-unit grid at a consistent 1.6 stroke so icons sit at the same
 * optical weight as the type around them. Everything is `currentColor` except
 * the Microsoft mark, which is brand artwork and keeps its own colours.
 */
type IconDef = { readonly d: string; readonly fill?: boolean };

const ICONS: Record<string, IconDef> = {
  'arrow-right': { d: 'M5 12h14M13 6l6 6-6 6' },
  'arrow-up-right': { d: 'M7 17 17 7M8 7h9v9' },
  'chevron-down': { d: 'm6 9 6 6 6-6' },
  'chevron-right': { d: 'm9 6 6 6-6 6' },
  check: { d: 'm4 12.5 5 5L20 6.5' },
  'check-circle': { d: 'M22 12a10 10 0 1 1-4.2-8.1M9 11.5l3 3L22 5' },
  plus: { d: 'M12 5v14M5 12h14' },
  minus: { d: 'M5 12h14' },
  'trend-up': { d: 'M3 17l6.5-6.5 4 4L21 7M15 7h6v6' },
  'trend-down': { d: 'M3 7l6.5 6.5 4-4L21 17M21 11v6h-6' },
  close: { d: 'M6 6l12 12M18 6 6 18' },
  menu: { d: 'M3 6h18M3 12h18M3 18h18' },
  home: { d: 'm3 10.6 9-7.2 9 7.2M5.6 9.4V20.2h12.8V9.4' },
  expand: { d: 'M4.5 9V4.5H9M15 4.5h4.5V9M19.5 15v4.5H15M9 19.5H4.5V15' },
  external: { d: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5' },

  sun: {
    d: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 1.5v2.2M12 20.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M1.5 12h2.2M20.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6',
  },
  moon: { d: 'M20.5 14.3A8.5 8.5 0 0 1 9.7 3.5a8.5 8.5 0 1 0 10.8 10.8Z' },

  // Product + concept icons
  'shield-user': {
    d: 'M12 22s8-3.4 8-9.4V5.6L12 2.5 4 5.6v7c0 6 8 9.4 8 9.4ZM12 11.6a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4ZM8.3 16.8a3.9 3.9 0 0 1 7.4 0',
  },
  scope: { d: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 2v3M12 19v3M2 12h3M19 12h3' },
  layers: { d: 'm12 3 9 4.5-9 4.5-9-4.5L12 3ZM3 12.4l9 4.5 9-4.5M3 16.9l9 4.5 9-4.5' },
  cpu: {
    d: 'M7 7h10v10H7V7ZM4.5 9.5H7M4.5 14.5H7M17 9.5h2.5M17 14.5h2.5M9.5 4.5V7M14.5 4.5V7M9.5 17v2.5M14.5 17v2.5',
  },
  bolt: { d: 'M13.5 2.5 4.8 13.2h6l-.3 8.3 8.7-10.7h-6l.3-8.3Z' },
  chart: { d: 'M4 20h16M7.5 20V11M12 20V5M16.5 20v-6' },
  grid: { d: 'M4 4h6.4v6.4H4V4ZM13.6 4H20v6.4h-6.4V4ZM4 13.6h6.4V20H4v-6.4ZM13.6 13.6H20V20h-6.4v-6.4Z' },
  search: { d: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.4-4.4' },
  flow: {
    d: 'M6 4.5h5.5a2 2 0 0 1 2 2v11a2 2 0 0 0 2 2H19M4 4.5h2M4 12h9.5M19 2.5v4M19 17.5v4M2.5 10v4M2.5 2.5v4',
  },
  eye: { d: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z' },
  lock: { d: 'M5.5 10.5h13v10h-13v-10ZM8.5 10.5V7a3.5 3.5 0 1 1 7 0v3.5M12 14.5v2.5' },
  card: { d: 'M3 6.5h18v11H3v-11ZM3 10.5h18M6.5 14.5h3' },
  users: {
    d: 'M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 19.5a6.5 6.5 0 0 1 13 0M16 4.8a3.5 3.5 0 0 1 0 6.7M18 14.2a6.5 6.5 0 0 1 3.5 5.3',
  },
  device: { d: 'M3 5h18v11H3V5ZM8 20h8M12 16v4' },
  key: { d: 'M15.5 12a4.5 4.5 0 1 0-4.4-5.5M11 6.5 2.5 15v4h4l1-1v-2h2v-2h2l1.6-1.6' },
  clock: { d: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5.3l3.4 2' },
  file: { d: 'M13.5 3H6.5v18h11V7l-4-4ZM13.5 3v4h4M9 12.5h6M9 16.5h6' },
  sparkle: { d: 'm12 3 1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4L12 3ZM19 16.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z' },
  network: {
    d: 'M12 8.5a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5ZM5 21a2.75 2.75 0 1 0 0-5.5A2.75 2.75 0 0 0 5 21ZM19 21a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5ZM12 8.5v4M12 12.5H5v3M12 12.5h7v3',
  },
  terminal: { d: 'M4.5 6.5 9 11l-4.5 4.5M12 17h7.5' },
  inbox: { d: 'M3.5 13.5h5l1.5 3h4l1.5-3h5M3.5 13.5 6 4.5h12l2.5 9v6h-17v-6Z' },
  alert: { d: 'M12 3.5 2.5 20h19L12 3.5ZM12 10v4M12 16.8v.2' },
  route: { d: 'M6.5 8a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5ZM17.5 21.5a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5ZM6.5 8v4a4 4 0 0 0 4 4h3a4 4 0 0 1 4 4' },
  refresh: { d: 'M20 12a8 8 0 1 1-2.6-5.9M20.5 3.5V9h-5.5' },
  building: { d: 'M4 21V4.5h9.5V21M13.5 21V9.5H20V21M2.5 21h19M7 8.5h3.5M7 12.5h3.5M7 16.5h3.5M16.5 13h1M16.5 17h1' },
  mail: { d: 'M3 6h18v12H3V6ZM3 6.5l9 6.5 9-6.5' },
};

@Component({
  selector: 'av-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.--size.px]': 'size()', class: 'av-icon-host' },
  template: `
    @if (name() === 'microsoft') {
      <svg
        [attr.width]="size()"
        [attr.height]="size()"
        viewBox="0 0 23 23"
        aria-hidden="true"
        focusable="false"
      >
        <path fill="#f25022" d="M1 1h10v10H1z" />
        <path fill="#7fba00" d="M12 1h10v10H12z" />
        <path fill="#00a4ef" d="M1 12h10v10H1z" />
        <path fill="#ffb900" d="M12 12h10v10H12z" />
      </svg>
    } @else if (def()) {
      <svg
        [attr.width]="size()"
        [attr.height]="size()"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        [attr.stroke-width]="weight()"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path [attr.d]="def()!.d" />
      </svg>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        line-height: 0;
        flex: none;
      }
      svg {
        display: block;
      }
    `,
  ],
})
export class Icon {
  readonly name = input.required<string>();
  readonly size = input(20, { transform: numberAttribute });
  readonly weight = input(1.6, { transform: numberAttribute });

  protected readonly def = computed<IconDef | undefined>(() => ICONS[this.name()]);
}
